import {
  Box,
  Card,
  FormatNumber,
  Heading,
  HStack,
  Image,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LottieReact } from "@lottielab/lottie-player";
import { useEffect, useState } from "react";
import { PiSealQuestionBold } from "react-icons/pi";
import { useNavigate } from "react-router";
import { useReadContract } from "wagmi";

import { AboutRewardsModal } from "./AboutRewardsModal";
import { ConvertRaffleTicketsBanner } from "./ConvertRaffleTicketsBanner";
import { CostPill } from "./CostPill";
import { DailySpinToaster } from "./DailySpin";
import { DailySpinHistory } from "./DailySpinHistory";
import { FortuneWheel } from "./FortuneWheel";
import { Raffle } from "./Raffle/Raffle";

import { BodyText } from "@/components/body-text";
import { GoonAvatar } from "@/components/goon-avatar";
import { HoverCardWithLink } from "@/components/hover-card-with-link";
import { RoundedBadge } from "@/components/rounded-badge";
import { RoundedButton } from "@/components/rounded-button";
import { DescriptionText } from "@/components/text/description";
import { DAILY_SPIN_BANNER_ID, dailySpinChainId } from "@/constants/shared";
import { useAuth } from "@/hooks/useAuth";
import { useDailySpinOffchain } from "@/hooks/useDailySpinOffchain";
import { useDailySpinOnchain } from "@/hooks/useDailySpinOnchain";
import { useHomeBanner } from "@/hooks/useHomeBanner";
import { DAILY_SPIN_ABI } from "@/network/abi/daily-spin";
import { RewardRecord } from "@/types";
import { truncateAddress } from "@/utils";

export const DailySpinActive = () => {
  const { dailySpinStreak } = useDailySpinOnchain();
  const { dismissBannerId } = useHomeBanner();
  const { address } = useAuth();
  const [localRecentSpinState, setLocalRecentSpinState] = useState<{
    address: string | undefined;
    reward: RewardRecord;
  }>();
  const localRecentSpin =
    localRecentSpinState && localRecentSpinState.address === address
      ? localRecentSpinState.reward
      : null;

  const { spinHistory, isLoading: isLoadingSpinHistory } =
    useDailySpinOffchain();
  useEffect(() => {
    dismissBannerId(DAILY_SPIN_BANNER_ID);
  }, [dismissBannerId]);

  const dailySpinHistory = (
    <VStack gap={4} alignItems="start" w="full" flex={1}>
      <Box>
        <HStack justifyContent="space-between" w="full" alignItems="center">
          <Heading mt={2} size="2xl">
            My Spins
          </Heading>
        </HStack>
        <DescriptionText fontSize="lg" fontFamily="heading">
          All spin costs go back into rewards
        </DescriptionText>
      </Box>
      <DailySpinHistory
        limit={100}
        data={spinHistory ?? []}
        localRecentSpin={localRecentSpin}
        isLoading={isLoadingSpinHistory}
      />
    </VStack>
  );

  return (
    <VStack gap={6} w="full">
      <ConvertRaffleTicketsBanner />
      <VStack gap={12} w="full">
        <Stack
          gap={12}
          direction={["column", "column", "column", "column", "row"]}
          w="full"
        >
          <VStack flex={1} alignItems={"start"} gap={6}>
            <HStack gap={4} alignItems="center">
              <VStack alignItems="start">
                <Heading
                  size="4xl"
                  fontWeight="700"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  The Daily Spin
                  <RoundedBadge py={1.5} px={4} hideBelow="md">
                    <Image src="/assets/daily-spin/lightning-bolt-streak.svg" />{" "}
                    {dailySpinStreak} Day Streak
                  </RoundedBadge>
                </Heading>
                <Box hideFrom="md">
                  <CostPill />
                </Box>
              </VStack>
            </HStack>
            <Box hideBelow="md" w="full">
              <JackpotCard />
            </Box>
            <Box
              w="full"
              h="full"
              flex={1}
              display="flex"
              flexDirection="column"
              hideBelow="md"
            >
              {dailySpinHistory}
            </Box>
          </VStack>
          <Box flex={1}>
            <FortuneWheel
              mostRecentReward={spinHistory?.[0]}
              recentRewardIsLoading={isLoadingSpinHistory}
              setLocalRecentSpin={(reward) =>
                setLocalRecentSpinState({ address, reward })
              }
            />
          </Box>
          <Box hideFrom="md" mt={-6}>
            <JackpotCard />
          </Box>
          <Box hideFrom="md">{dailySpinHistory}</Box>
        </Stack>
        <Raffle />
        <DailySpinToaster />
      </VStack>
    </VStack>
  );
};

const JackpotCard = () => {
  const [isRewardsModalOpen, setIsRewardsModalOpen] = useState(false);
  const { data, isLoading: isLoadingJackpotPrize } = useReadContract({
    address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
    abi: DAILY_SPIN_ABI,
    chainId: dailySpinChainId,
    functionName: "getWeeklyJackpot",
    query: {
      refetchOnMount: false,
    },
  });

  const navigate = useNavigate();

  const { lastWeekRaffleWinner } = useDailySpinOffchain();

  // data is an array of [weekNumber, jackpotPrize, requiredStreak] - all values need to be parsed
  const [, jackpotPrize, requiredStreak] = data ?? [];
  const [isHovering, setIsHovering] = useState(false);
  return (
    <HoverCardWithLink
      href={null}
      cardRootStyles={{
        cursor: "pointer",
        borderRadius: "2xl",
        overflow: "hidden",
        bgColor: "#390E28",
        w: "full",
        onMouseEnter: () => {
          setIsHovering(true);
        },
        onMouseLeave: () => {
          setIsHovering(false);
        },
      }}
    >
      <Card.Body
        color="fg.inverted"
        position="relative"
        onClick={() => {
          setIsRewardsModalOpen(true);
        }}
      >
        <Box position="absolute" inset={0} zIndex={0}>
          <LottieReact
            src="https://cdn.lottielab.com/l/DJbyGoaXLLDwy9.json"
            style={{
              position: "absolute",
              minHeight: "100%",
              minWidth: "100%",
              aspectRatio: 1024 / 360,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
            playing={isHovering}
          />
        </Box>
        <Heading color="fg.inverted" position="relative" zIndex={1}>
          This week&apos;s prize:{" "}
          {isLoadingJackpotPrize ? (
            <Spinner />
          ) : (
            <FormatNumber
              value={Number(jackpotPrize)}
              notation="compact"
              compactDisplay="short"
            />
          )}{" "}
          PLUME{" "}
        </Heading>
        <Text fontWeight="600" opacity=".7" mb={4}>
          Maintain a {isLoadingJackpotPrize ? <Spinner /> : requiredStreak}-day
          spin streak to qualify!
        </Text>
        <RoundedButton
          bgColor="#703F5C"
          _hover={{ opacity: "0.8" }}
          maxW={200}
          h={12}
        >
          <PiSealQuestionBold />
          Learn About Rewards
        </RoundedButton>
        <AboutRewardsModal
          isOpen={isRewardsModalOpen}
          onClose={() => setIsRewardsModalOpen(false)}
        />
      </Card.Body>
      {lastWeekRaffleWinner && (
        <Card.Footer
          py={4}
          bgColor="#390E28"
          justifyContent="space-between"
          mt={[0, 0, 0, 0, 3]}
          onClick={() =>
            navigate(`/profile/${lastWeekRaffleWinner.walletAddress}`)
          }
        >
          <Box>
            <BodyText color="fg.inverted" fontSize="md">
              Last week&apos;s winner
            </BodyText>
            <BodyText color="#FFFFFF99" fontSize="sm">
              <FormatNumber
                value={lastWeekRaffleWinner.rewardAmount}
                notation="compact"
              />{" "}
              PLUME
            </BodyText>
          </Box>
          <RoundedButton bgColor="#FFFFFF1A">
            <GoonAvatar boxSize={6} seed={lastWeekRaffleWinner.walletAddress} />
            {truncateAddress(lastWeekRaffleWinner.walletAddress)}
          </RoundedButton>
        </Card.Footer>
      )}
    </HoverCardWithLink>
  );
};
